import { uuidv4 } from "./helpers";
import { LighthouseData, Sector } from "./lighthouseConstants";
import { NFSStatus, getNewStatus } from "./nfsUtils";
import { SectorDraft, sameLengthInMeters, sectorDraftsToSectors } from "./sectorDraft";

/** Bumps a status the way an edit in the sector editor does. */
const marked = (status: number) => getNewStatus(status, NFSStatus.PROJECTED_EXISTING);

/**
 * Which parts of a sector an edit touched. A boundary line belongs to two sectors, so an edit
 * to one of them has to mark its neighbour as well - the same bookkeeping SectorMenu does field
 * by field, worked out here by comparing the row against the sector it came from.
 */
interface SectorChanges {
    /** The sector itself: colour, radius or description. */
    sector: boolean;
    /** Where the sector starts - moves the boundary line it shares with the previous sector. */
    startAngle: boolean;
    /** The length of that shared boundary line. */
    startLineLength: boolean;
}

const changesBetween = (before: Sector, after: Sector): SectorChanges => ({
    sector:
        before.color !== after.color ||
        (before.description ?? "") !== (after.description ?? "") ||
        !sameLengthInMeters(before.innerRadius, after.innerRadius),
    startAngle: before.startAngle !== after.startAngle,
    startLineLength: !sameLengthInMeters(before.startLineLength, after.startLineLength),
});

/**
 * Replaces a lighthouse's sectors with the ones the definition table produced, keeping the
 * identity and history of everything that was already there.
 *
 * A row seeded from an existing sector keeps that sector's globalid, so saveLighthouse updates
 * the row rather than discontinuing it and creating a replacement - which is what would happen
 * to every sector of the lighthouse otherwise. A row the user added gets a fresh globalid and
 * the projected statuses of a new sector; a sector whose row was deleted is simply absent, and
 * saveLighthouse discontinues it.
 *
 * Statuses of surviving sectors are carried over from the database and then marked per changed
 * field, matching the sector editor - so a lighthouse edited through the table ends up with the
 * same statuses it would have had if the same edits had been made sector by sector.
 */
export const applyDraftsToLighthouse = (existing: LighthouseData, drafts: SectorDraft[]): LighthouseData => {
    const before = new Map(
        existing.sectors.filter((sector) => sector.globalid).map((sector) => [sector.globalid, sector])
    );

    const sectors = sectorDraftsToSectors(drafts).map((sector) => {
        const original = sector.globalid ? before.get(sector.globalid) : undefined;

        if (!original) {
            return { ...sector, globalid: uuidv4(), lighthouseId: existing.id };
        }

        return {
            ...sector,
            lighthouseId: existing.id,
            nfs_status: original.nfs_status,
            nfs_status_line1: original.nfs_status_line1,
            nfs_status_line2: original.nfs_status_line2,
        };
    });

    for (const [index, sector] of sectors.entries()) {
        const original = before.get(sector.globalid);
        if (!original) continue;

        const changes = changesBetween(original, sector);
        // The sector before this one owns the other side of the boundary line that moved
        const previous = sectors[(index - 1 + sectors.length) % sectors.length];

        if (changes.sector || changes.startAngle) {
            sector.nfs_status = marked(sector.nfs_status);
        }
        if (changes.startAngle || changes.startLineLength) {
            sector.nfs_status_line1 = marked(sector.nfs_status_line1);
            previous.nfs_status_line2 = marked(previous.nfs_status_line2);
        }
        if (changes.startAngle && previous !== sector) {
            previous.nfs_status = marked(previous.nfs_status);
        }
    }

    return { ...existing, sectors };
};
